import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { collection, doc, getDoc, getDocs, setDoc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import emailjs from "@emailjs/browser";
import CompanyManagement from "./CompanyManagement";
import { InquiryAdmin } from "./Inquiry";

// ========== EmailJS設定 ==========
function EmailSettings() {
  const [cfg, setCfg] = useState({ emailjsServiceId: "", emailjsTemplateId: "", emailjsPublicKey: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState("");

  useEffect(() => {
    getDoc(doc(db, "notifySettings", "system")).then(snap => {
      if (snap.exists()) setCfg(c => ({ ...c, ...snap.data() }));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    const snap = await getDoc(doc(db, "notifySettings", "system"));
    const base = snap.exists() ? snap.data() : {};
    await setDoc(doc(db, "notifySettings", "system"), {
      ...base,
      emailjsServiceId:  cfg.emailjsServiceId,
      emailjsTemplateId: cfg.emailjsTemplateId,
      emailjsOperatorTemplateId: cfg.emailjsOperatorTemplateId || "",
      emailjsPublicKey:  cfg.emailjsPublicKey,
      updatedAt: Timestamp.now(),
    });
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  // その場で送信を試す。届かないときに、どこで失敗しているかを切り分けるため。
  // 画面に入力されている値をそのまま使うので、保存前でも試せる。
  // kind: "operator"（運営あて）/ "document"（書類申請の通知）
  const sendTest = async (kind) => {
    setTesting(true); setTestMsg("");

    const isDoc = kind === "document";
    const templateId = isDoc ? cfg.emailjsTemplateId : cfg.emailjsOperatorTemplateId;
    const myEmail = auth.currentUser?.email || "";

    const missing = [];
    if (!cfg.emailjsServiceId) missing.push("Service ID");
    if (!templateId) missing.push(isDoc ? "Template ID（書類申請の通知用）" : "Template ID（運営への通知用）");
    if (!cfg.emailjsPublicKey) missing.push("Public Key");
    if (missing.length > 0) {
      setTestMsg(`⚠️ 未入力の欄があります：${missing.join(" / ")}`);
      setTesting(false);
      return;
    }

    // 書類申請の通知は宛先が変数なので、確認用に運営自身あてに送る
    const params = isDoc
      ? {
          to_email:   myEmail,
          from_name:  myEmail,
          doc_type:   "テスト送信",
          doc_date:   "（テスト）",
          doc_reason: "書類申請の通知テンプレートのテストです。これが届いていれば設定は正しく動いています。",
        }
      : {
          event_type:   "テスト送信",
          company_name: "（テスト）",
          user_name:    "テスト",
          user_email:   "test@example.com",
          detail:       "メール送信設定のテストです。これが届いていれば設定は正しく動いています。",
        };

    try {
      await emailjs.send(cfg.emailjsServiceId, templateId, params, cfg.emailjsPublicKey);
      setTestMsg(`✅ 送信しました（${isDoc ? "書類申請の通知用" : "運営への通知用"}）。${myEmail || "受信箱"} を確認してください。`);
    } catch (err) {
      console.error("テスト送信に失敗しました:", err);
      setTestMsg(`❌ 失敗（${isDoc ? "書類申請の通知用" : "運営への通知用"}）：${err?.text || err?.message || JSON.stringify(err)}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={st.formWrap}>
      <h2 style={st.pageTitle}>📧 メール送信設定（全社共通）</h2>
      <p style={st.desc}>
        ここで設定したEmailJS情報を全会社のメール通知で使用します。各会社の管理者はオン/オフと通知先メールのみ設定します。
        「書類申請の通知用」は各社の管理者あて、「運営への通知用」は運営あてに届きます。
      </p>

      <div style={st.card}>
        <label style={st.label}>Service ID</label>
        <input style={st.input} placeholder="service_xxxxxxx"
          value={cfg.emailjsServiceId} onChange={e => set("emailjsServiceId", e.target.value)} />

        <label style={st.label}>Template ID（書類申請の通知用）</label>
        <input style={st.input} placeholder="template_xxxxxxx"
          value={cfg.emailjsTemplateId} onChange={e => set("emailjsTemplateId", e.target.value)} />

        <label style={st.label}>Template ID（運営への通知用）</label>
        <input style={st.input} placeholder="template_xxxxxxx（新規登録・お問い合わせを運営へ知らせる）"
          value={cfg.emailjsOperatorTemplateId || ""} onChange={e => set("emailjsOperatorTemplateId", e.target.value)} />

        <label style={st.label}>Public Key（公開鍵）</label>
        <input style={st.input} placeholder="xxxxxxxxxxxxxxxxxxxxxx"
          value={cfg.emailjsPublicKey} onChange={e => set("emailjsPublicKey", e.target.value)} />

        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "8px", flexWrap: "wrap" }}>
          <button style={st.saveBtn} onClick={save} disabled={saving}>
            {saving ? "保存中..." : "保存する"}
          </button>
          <button
            style={{ padding: "12px 20px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "14px" }}
            onClick={() => sendTest("operator")} disabled={testing}>
            {testing ? "送信中..." : "テスト送信（運営あて）"}
          </button>
          <button
            style={{ padding: "12px 20px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "14px" }}
            onClick={() => sendTest("document")} disabled={testing}>
            {testing ? "送信中..." : "テスト送信（書類申請）"}
          </button>
          {saved && <span style={{ color: "#27ae60" }}>✅ 保存しました</span>}
        </div>

        {testMsg && (
          <p style={{ fontSize: "13px", lineHeight: "1.9", wordBreak: "break-all", backgroundColor: "#f7f9fb", borderRadius: "8px", padding: "12px 14px", marginTop: "4px" }}>
            {testMsg}
          </p>
        )}
        <p style={{ fontSize: "12px", color: "#7f8c8d", lineHeight: "1.8" }}>
          「テスト送信」は、いま画面に入力されている値をそのまま使って1通送ります。保存前でも試せます。
          書類申請の通知は本来なら各社の管理者あてに届きますが、テストではご自身のアドレスに送ります。
        </p>
      </div>
    </div>
  );
}

// ========== 2段階認証の解除 ==========
// 携帯を機種変更・紛失した管理者から連絡が来たときに使う。
function MfaReset() {
  const [email, setEmail]   = useState("");
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState("");
  const [error, setError]   = useState("");
  const [required, setRequired] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "notifySettings", "system")).then(snap => {
      if (snap.exists()) setRequired(snap.data().mfaRequired === true);
    }).catch(e => console.error("2段階認証の設定の取得に失敗しました:", e));
  }, []);

  const toggleRequired = async () => {
    const next = !required;
    if (next && !window.confirm(
      "管理者に2段階認証を求めます。\n\n" +
      "Firebaseコンソールで Identity Platform へのアップグレードとSMS多要素認証の有効化が" +
      "済んでいないと、管理者は登録できません。済んでいますか。"
    )) return;

    setSavingFlag(true);
    try {
      const ref = doc(db, "notifySettings", "system");
      const snap = await getDoc(ref);
      await setDoc(ref, { ...(snap.exists() ? snap.data() : {}), mfaRequired: next, updatedAt: Timestamp.now() });
      setRequired(next);
    } catch (e) {
      console.error("2段階認証の設定の保存に失敗しました:", e);
      alert("保存に失敗しました");
    } finally {
      setSavingFlag(false);
    }
  };

  const run = async () => {
    const addr = email.trim();
    if (!addr) return;
    if (!window.confirm(`${addr} の2段階認証を解除します。ご本人からの依頼であることを確認しましたか。`)) return;

    setBusy(true); setResult(""); setError("");
    try {
      const call = httpsCallable(getFunctions(undefined, "us-central1"), "resetMfa");
      const res = await call({ email: addr });
      setResult(res.data.remaining === 0
        ? "解除しました。ご本人が次にログインしたときに、携帯電話の登録をやり直していただいてください。"
        : `解除しきれませんでした。まだ${res.data.remaining}件残っています。`);
    } catch (e) {
      console.error("2段階認証の解除に失敗しました:", e);
      setError(e?.message || "解除に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={st.formWrap}>
      <h2 style={st.pageTitle}>🔐 2段階認証</h2>
      <p style={st.desc}>
        上は「これから求めるかどうか」の切り替え、下は「登録済みの人の解除」です。
        <strong>切り替えを「求めない」にしても、すでに登録された方の確認コードは無くなりません。</strong>
        その場合は下の解除をお使いください。
      </p>

      <div style={st.card}>
        <label style={st.label}>① 管理者に2段階認証を求める</label>
        <p style={{ fontSize: "13px", color: "#7f8c8d", lineHeight: "1.9", margin: "0 0 10px" }}>
          現在：<strong style={{ color: required ? "#27ae60" : "#7f8c8d" }}>{required ? "求めている" : "求めていない"}</strong>
          <br />
          対象は各社の会社管理者です（運営は対象外）。初回は{" "}
          <strong>14日間の猶予</strong>があり、その間は「あとで設定する」を選べます。
        </p>
        <button style={{ ...st.saveBtn, backgroundColor: required ? "#c0392b" : "#27ae60" }}
          onClick={toggleRequired} disabled={savingFlag}>
          {savingFlag ? "保存中..." : required ? "求めるのをやめる" : "求めるようにする"}
        </button>
      </div>

      <div style={st.card}>
        <label style={st.label}>② 登録を解除する（機種変更・紛失時）</label>
        <p style={{ fontSize: "12px", color: "#7f8c8d", lineHeight: "1.8", margin: "0 0 8px" }}>
          対象のメールアドレスを入力してください。そのアカウントの携帯電話の登録を消し、
          ログイン時の確認コードを不要にします。
        </p>
        <input style={st.input} type="email" placeholder="admin@example.com"
          value={email} onChange={e => setEmail(e.target.value)} />

        <p style={{ fontSize: "12px", color: "#8a5a00", backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "8px", padding: "10px 12px", lineHeight: "1.8" }}>
          解除するとパスワードだけでログインできる状態に戻ります。
          必ず、ご本人からの依頼であることを確認してから実行してください。
        </p>

        <button style={st.saveBtn} onClick={run} disabled={busy || !email.trim()}>
          {busy ? "解除中..." : "解除する"}
        </button>
        {result && <p style={{ color: "#27ae60", fontSize: "13px", lineHeight: "1.8" }}>{result}</p>}
        {error  && <p style={{ color: "#c0392b", fontSize: "13px", lineHeight: "1.8" }}>{error}</p>}
      </div>
    </div>
  );
}

// ========== 運営ダッシュボード ==========
function OperatorDashboard({ user }) {
  const [page, setPage] = useState("home");
  const [openInquiries, setOpenInquiries] = useState(0);

  // 未対応のお問い合わせ件数。画面を移動するたびに取り直す。
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "inquiries"));
        const count = snap.docs.filter(d => d.data().status !== "done").length;
        if (alive) setOpenInquiries(count);
      } catch (e) {
        console.error("お問い合わせ件数の取得に失敗しました:", e);
      }
    })();
    return () => { alive = false; };
  }, [page]);

  const renderPage = () => {
    switch (page) {
      case "company": return <CompanyManagement />;
      case "email":   return <EmailSettings />;
      case "inquiry": return <InquiryAdmin onCountChange={setOpenInquiries} />;
      case "security": return <MfaReset />;
      default:        return <OperatorHome setPage={setPage} openInquiries={openInquiries} />;
    }
  };

  return (
    <div style={st.container}>
      <header style={st.header}>
        <h1 style={st.logo} onClick={() => setPage("home")}>
          miyabi Company <span style={st.badge}>運営管理</span>
        </h1>
        <div style={st.userInfo}>
          <span style={st.email}>{user.email}</span>
          <button style={st.logoutBtn} onClick={() => signOut(auth)}>ログアウト</button>
        </div>
      </header>
      <main>{renderPage()}</main>
    </div>
  );
}

function OperatorHome({ setPage, openInquiries = 0 }) {
  const menus = [
    { key: "company", icon: "🏢", title: "会社管理",       desc: "契約会社の追加・停止・プラン管理" },
    { key: "email",   icon: "📧", title: "メール送信設定", desc: "全社共通のEmailJS設定" },
    { key: "inquiry", icon: "✉️", title: "お問い合わせ",
      desc: openInquiries > 0 ? `未対応が${openInquiries}件あります` : "利用者からの問い合わせ・返信",
      badge: openInquiries },
    { key: "security", icon: "🔐", title: "2段階認証", desc: "必須化の切り替えと、紛失時の解除" },
  ];

  return (
    <div style={st.main}>
      <h2 style={st.welcome}>運営管理ダッシュボード</h2>
      <p style={st.desc}>このページは運営者専用です。</p>
      <div style={st.grid}>
        {menus.map(m => (
          <div key={m.key} style={{ ...st.card, ...(m.badge > 0 ? st.cardAlert : {}) }} onClick={() => setPage(m.key)}>
            {m.badge > 0 && <span style={st.cardBadge}>{m.badge}</span>}
            <div style={st.cardIcon}>{m.icon}</div>
            <h3 style={{ color: "#2c3e50", margin: "0 0 8px" }}>{m.title}</h3>
            <p style={st.cardDesc}>{m.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const st = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  header: { backgroundColor: "#1a252f", color: "#fff", padding: "16px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  logo: { margin: 0, fontSize: "20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" },
  badge: { fontSize: "12px", backgroundColor: "#e74c3c", color: "#fff", padding: "3px 10px", borderRadius: "20px", fontWeight: "normal" },
  userInfo: { display: "flex", alignItems: "center", gap: "16px" },
  email: { fontSize: "14px", opacity: 0.8 },
  logoutBtn: { padding: "8px 16px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" },
  main: { padding: "40px 32px", maxWidth: "900px", margin: "0 auto" },
  welcome: { color: "#2c3e50", marginBottom: "8px" },
  desc: { color: "#7f8c8d", marginBottom: "32px", fontSize: "14px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" },
  card: { position: "relative", backgroundColor: "#fff", padding: "32px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textAlign: "center", cursor: "pointer" },
  cardAlert: { border: "2px solid #e74c3c" },
  cardBadge: { position: "absolute", top: "12px", right: "12px", minWidth: "22px", padding: "2px 8px", borderRadius: "12px", backgroundColor: "#e74c3c", color: "#fff", fontSize: "13px", fontWeight: "bold", lineHeight: "18px" },
  cardIcon: { fontSize: "48px", marginBottom: "12px" },
  cardDesc: { color: "#7f8c8d", fontSize: "14px", margin: 0 },
  // EmailSettings
  formWrap: { padding: "32px", maxWidth: "600px", margin: "0 auto" },
  pageTitle: { color: "#2c3e50", marginBottom: "8px" },
  label: { display: "block", marginBottom: "6px", color: "#2c3e50", fontSize: "14px", fontWeight: "bold" },
  input: { width: "100%", padding: "11px 14px", marginBottom: "16px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  saveBtn: { padding: "12px 32px", backgroundColor: "#1a252f", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
};

export default OperatorDashboard;
